import React from "react";
import { Button, Card, Col, Form, Row, Select, Space, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import i18n from "translations/i18n";
import AvatarStatus from "components/shared-components/AvatarStatus";
import QuantityControl from "components/shared-components/QuantityControl";
import { formatPrice } from "functions/formatters";
import styled from "styled-components";

const { Option } = Select;
const { Text } = Typography;

const BundleItemsSelector = ({ form, products, disabled }) => {
  const isBundle = Form.useWatch("isBundle", form);
  const calculatedItemPrices = Form.useWatch("calculatedItemPrices", form) || {};

  // Filter out bundles and packaging from selectable products
  const selectableProducts = products.filter(
    (p) =>
      !p.isBundle &&
      p.productGroup !== "Returnable packaging" &&
      p.productGroup !== "Bundles" &&
      p.productGroup !== "Packaging",
  );

  const getProductById = (productId) => {
    return products.find((p) => p.productId === productId);
  };

  const handleQuantityIncrease = (name) => {
    const bundleItems = form.getFieldValue("bundleItems");
    const currentQty = bundleItems[name].quantity || 0;
    form.setFieldsValue({
      bundleItems: bundleItems.map((item, idx) => (idx === name ? { ...item, quantity: currentQty + 1 } : item)),
    });
  };

  const handleQuantityDecrease = (name) => {
    const bundleItems = form.getFieldValue("bundleItems");
    const currentQty = bundleItems[name].quantity || 0;
    if (currentQty > 1) {
      form.setFieldsValue({
        bundleItems: bundleItems.map((item, idx) => (idx === name ? { ...item, quantity: currentQty - 1 } : item)),
      });
    }
  };

  const handleQuantityManualInput = (name, value) => {
    const bundleItems = form.getFieldValue("bundleItems");
    form.setFieldsValue({
      bundleItems: bundleItems.map((item, idx) => (idx === name ? { ...item, quantity: value } : item)),
    });
  };

  const calculateTotalItems = () => {
    const bundleItems = form.getFieldValue("bundleItems") || [];
    return bundleItems.reduce((sum, item) => sum + (item?.quantity || 0), 0);
  };

  const calculateTotalPrice = () => {
    const bundleItems = form.getFieldValue("bundleItems") || [];
    return bundleItems.reduce((sum, item) => {
      const calculatedPrice = calculatedItemPrices[item?.productId];
      if (calculatedPrice !== undefined) {
        return sum + calculatedPrice * (item?.quantity || 0);
      }
      // Fallback to original price if calculated price not available
      const product = getProductById(item?.productId);
      const itemPrice = product ? product.price || 0 : 0;
      return sum + itemPrice * (item?.quantity || 0);
    }, 0);
  };

  if (!isBundle) return null;

  return (
    <Card title={i18n.t("bundle_contents")}>
      <Form.List name="bundleItems">
        {(fields, { add, remove }) => (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {fields.map((field) => {
              const bundleItems = form.getFieldValue("bundleItems") || [];
              const item = bundleItems[field.name] || {};
              const product = getProductById(item.productId);
              const originalPrice = product ? product.price || 0 : 0;
              const calculatedPrice = calculatedItemPrices[item.productId];
              const hasCustomPrice = calculatedPrice !== undefined && calculatedPrice !== originalPrice;

              return (
                <BundleItemRow key={field.key} gutter={16} align="middle">
                  <Col xs={24} md={10}>
                    <Form.Item name={[field.name, "productId"]} noStyle>
                      <Select
                        showSearch
                        style={{ width: "100%" }}
                        placeholder={i18n.t("form.select_product")}
                        optionFilterProp="children"
                        disabled={disabled}
                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                      >
                        {selectableProducts.map((p) => (
                          <Option key={p.productId} value={p.productId}>
                            {p.productName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={12} md={6}>
                    {product && (
                      <AvatarStatus
                        size={40}
                        type="square"
                        src={product.productImage512DownloadURL?.[0]}
                        name={product.productId}
                      />
                    )}
                  </Col>

                  <Col xs={12} md={4}>
                    <Form.Item name={[field.name, "quantity"]} noStyle>
                      <QuantityControl
                        productId={field.name}
                        currentQuantity={item.quantity || 1}
                        onIncrease={() => handleQuantityIncrease(field.name)}
                        onDecrease={() => handleQuantityDecrease(field.name)}
                        onManualInput={(_, value) => handleQuantityManualInput(field.name, value)}
                        disabled={disabled}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={12} md={3}>
                    {product && (
                      <PriceDisplay>
                        {hasCustomPrice ? (
                          <>
                            <StrikethroughPrice>
                              €{formatPrice(product.price || 0)}
                            </StrikethroughPrice>
                            <NewPrice>€{formatPrice(calculatedPrice)}</NewPrice>
                          </>
                        ) : (
                          <Text strong>€{formatPrice(product.price || 0)}</Text>
                        )}
                      </PriceDisplay>
                    )}
                  </Col>

                  <Col xs={12} md={1}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                      disabled={disabled}
                    />
                  </Col>
                </BundleItemRow>
              );
            })}

            <Button
              type="dashed"
              onClick={() => add({ productId: null, quantity: 1 })}
              block
              icon={<PlusOutlined />}
              disabled={disabled}
            >
              {i18n.t("add_bundle_item")}
            </Button>

            {fields.length > 0 && (
              <SummaryRow>
                <Col span={12}>
                  <Text strong>{i18n.t("total_items")}:</Text> {calculateTotalItems()}
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                  <Text strong>{i18n.t("total_price")}:</Text> €{formatPrice(calculateTotalPrice())}
                </Col>
              </SummaryRow>
            )}
          </Space>
        )}
      </Form.List>
    </Card>
  );
};

const BundleItemRow = styled(Row)`
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
`;

const SummaryRow = styled(Row)`
  padding: 12px;
  border-radius: 8px;
  margin-top: 8px;
`;

const PriceDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StrikethroughPrice = styled(Text)`
  text-decoration: line-through;
  color: #999;
  font-size: 12px;
`;

const NewPrice = styled(Text)`
  font-weight: 600;
  color: #52c41a;
  font-size: 14px;
`;

export default BundleItemsSelector;
